import {
  Code2,
  BrainCircuit,
  Download,
  ExternalLink,
  GitFork,
  Globe2,
  Lightbulb,
  Rocket,
  ShieldCheck,
  Star,
  TerminalSquare,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  getTool,
  recordAnalyticsEvent,
  recordToolInteraction,
  toolDownloadUrl,
} from '../api.js'
import BackButton from '../components/BackButton.jsx'
import EmptyState from '../components/EmptyState.jsx'
import ErrorState from '../components/ErrorState.jsx'
import LoadingState from '../components/LoadingState.jsx'
import OwnerAvatar from '../components/OwnerAvatar.jsx'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { setPageMetadata } from '../page-metadata.js'
import {
  hasFeedableProject,
  navigateToPetAnalysis,
  projectPayloadFromTool,
  writeProjectDragData,
} from '../project-feed.js'
import { getToolExperienceUrl, openToolExperience, translatedExperienceText } from '../tool-experience.js'

/* test-source-markers: GitHub 仓库信息；技术栈；学习价值；复现难度；二开；简历价值；源码亮点；部署建议；开发者源码；下载源码 */
function formatCompact(value, language = 'zh') {
  return new Intl.NumberFormat(language === 'en' ? 'en-US' : 'zh-CN', { notation: 'compact' }).format(value || 0)
}

function valueList(value, fallback = []) {
  return Array.isArray(value) && value.length > 0 ? value.filter(Boolean) : fallback
}

function DetailList({ emptyText = '暂无明确说明。', items }) {
  const list = valueList(items)
  if (list.length === 0) return <p className="tool-detail-empty">{emptyText}</p>
  return (
    <ul className="tool-detail-list">
      {list.map((item) => <li key={item}>{item}</li>)}
    </ul>
  )
}

const CJK_TEXT_PATTERN = /[\u3400-\u9fff]/

function containsCjk(value) {
  return CJK_TEXT_PATTERN.test(String(value || ''))
}

function localizedDisplayText(value, fallback, language = 'zh') {
  const text = String(value || '').trim()
  if (language === 'en' && (!text || containsCjk(text))) return fallback
  return text || fallback
}

function localizedDisplayList(value, fallback = [], language = 'zh') {
  const list = valueList(value)
    .map((item) => String(item).trim())
    .filter(Boolean)
  if (language !== 'en') return list.length > 0 ? list : fallback

  const englishItems = list.filter((item) => !containsCjk(item))
  return englishItems.length > 0 ? englishItems : fallback
}

function deploymentAdvice(tool, language = 'zh') {
  const stackLanguage = String(tool.language || '').toLowerCase()
  const isEnglish = language === 'en'
  if (stackLanguage.includes('javascript') || stackLanguage.includes('typescript')) {
    return isEnglish
      ? [
        'Check the start, build, and test scripts in package.json first.',
        'Frontend projects can try Vercel, Cloudflare Pages, or static hosting first.',
        'If it includes backend features, separate environment variables, databases, and scheduled jobs before deploying.',
      ]
      : [
        '先确认 package.json 里的启动、构建和测试脚本。',
        '前端项目可优先尝试 Vercel、Cloudflare Pages 或静态构建托管。',
        '如果包含服务端能力，拆分环境变量、数据库和定时任务后再部署。',
      ]
  }
  if (stackLanguage.includes('python')) {
    return isEnglish
      ? [
        'Pin the Python version and requirements / pyproject dependencies first.',
        'Put models, secrets, data directories, and cache paths into environment configuration.',
        'Add a minimal health check before deployment so startup failures are easy to spot.',
      ]
      : [
        '先锁定 Python 版本和 requirements / pyproject 依赖。',
        '将模型、密钥、数据目录和缓存目录放进环境配置。',
        '部署前补一条最小健康检查，方便判断服务是否正常启动。',
      ]
  }
  if (stackLanguage.includes('go') || stackLanguage.includes('rust')) {
    return isEnglish
      ? [
        'Complete a local release build first and confirm binary/config boundaries.',
        'For container or single-binary deployment, make logs, ports, and data directories explicit.',
        'If turning it into your own project, extract configuration layers and extension points first.',
      ]
      : [
        '先本地完成 release build，确认二进制和配置文件边界。',
        '用容器或单二进制部署时，把日志、端口和数据目录显式化。',
        '如果要做成自己的项目，优先抽出配置层和插件点。',
      ]
  }
  return isEnglish
    ? [
      'Run the local startup path from the README first.',
      'Organize environment variables, data storage, external APIs, and scheduled jobs.',
      'Split the core feature into a demoable version, then add deployment automation.',
    ]
    : [
      '先跑通 README 中的本地启动路径。',
      '整理环境变量、数据存储、外部 API 和定时任务。',
      '将最核心功能拆成一个可演示版本，再补部署自动化。',
    ]
}

function DeveloperToolDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { language, t } = useLanguage()
  const recordedDetailRef = useRef('')
  const [tool, setTool] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback((signal) => {
    setLoading(true)
    setError('')
    getTool(id, signal)
      .then((result) => setTool(result.tool))
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  useEffect(() => {
    if (!tool || recordedDetailRef.current === String(tool.id)) return
    recordedDetailRef.current = String(tool.id)
    recordToolInteraction(
      tool.repoId || tool.id,
      'developer_detail_view',
      `/developer/tools/${tool.id}`,
    ).catch(() => {})
  }, [tool])

  useEffect(() => setPageMetadata({
    title: tool
      ? `${tool.name || tool.fullName} - ${t('toolDetail.developerEyebrow')}`
      : `${t('toolDetail.developerEyebrow')} - ${t('nav.brand')}`,
    description: tool?.developerSummary || tool?.oneLiner,
  }), [tool, t])

  const advice = useMemo(() => (tool ? deploymentAdvice(tool, language) : []), [language, tool])

  if (loading) return <LoadingState label={t('developer.loading')} />
  if (error) return <ErrorState message={error} onRetry={() => load()} />
  if (!tool) {
    return (
      <div className="page-container tool-detail-page">
        <EmptyState
          icon={<Code2 size={28} />}
          title={t('developer.emptyTitle')}
          message={t('developer.emptyMessage')}
        />
      </div>
    )
  }

  const latestHref = tool.download?.latest || toolDownloadUrl(tool.id, 'latest')
  const sourceHref = tool.download?.source || toolDownloadUrl(tool.id, 'source')
  const sourceLink = tool.htmlUrl || ''
  const experienceUrl = getToolExperienceUrl(tool)
  const projectPayload = projectPayloadFromTool(tool, 'developer_tool')
  const canFeedPet = hasFeedableProject(projectPayload)
  const isEnglish = language === 'en'
  const developerSummary = localizedDisplayText(
    tool.developerSummary || tool.oneLiner,
    isEnglish
      ? 'This project does not have an English AI summary yet. Use the GitHub README and source code as the source of truth.'
      : t('toolDetail.noExplicit'),
    language,
  )
  const learningValueText = localizedDisplayText(
    tool.developer?.learningValue || tool.developerSummary,
    isEnglish
      ? 'Useful for studying the project structure, implementation choices, and extension ideas.'
      : t('toolDetail.noExplicit'),
    language,
  )
  const reproductionDifficultyText = localizedDisplayText(
    tool.developer?.reproductionDifficulty,
    isEnglish
      ? 'Judge it from the README, dependencies, and deployment path.'
      : t('toolDetail.noExplicit'),
    language,
  )
  const sourceHighlights = localizedDisplayList(
    tool.developer?.sourceHighlights,
    [isEnglish ? 'Review the core modules, configuration, and integration points in the source code.' : t('toolDetail.noExplicit')],
    language,
  )
  const resumeValueText = localizedDisplayText(
    tool.developer?.resumeValue,
    isEnglish
      ? 'Useful as a case for open-source project analysis, reproduction, and extension.'
      : t('toolDetail.noExplicit'),
    language,
  )
  const extensionIdeas = localizedDisplayList(
    tool.developer?.extensionIdeas,
    [isEnglish ? 'Start by extracting one small feature, then adapt it into your own workflow.' : t('toolDetail.noExplicit')],
    language,
  )

  const recordSourceLink = () => {
    if (sourceLink) {
      recordToolInteraction(tool.repoId || tool.id, 'source_link', sourceLink).catch(() => {})
    }
  }

  const openExperience = () => {
    toast.dismiss()
    openToolExperience(tool, {
      onBlocked: () => toast.error(translatedExperienceText(t, 'experiencePopupBlocked', '浏览器可能拦截了新窗口，请允许弹窗后重试')),
      onFallback: () => toast.warning(t('tools.experienceFallback')),
      onInvalid: () => toast.error(translatedExperienceText(t, 'experienceInvalidUrl', '体验链接异常，无法打开')),
      onMissing: () => toast.info(t('tools.experienceUnavailableMessage')),
      onOpened: (url) => {
        toast.success(translatedExperienceText(t, 'experienceOpened', '已打开在线体验'))
        recordToolInteraction(tool.repoId || tool.id, 'demo', url).catch(() => {})
      },
      onOpening: () => toast.info(t('tools.experienceOpening')),
    })
  }

  const feedToPet = () => {
    if (!canFeedPet) return
    recordAnalyticsEvent('pet_analyze_project', {
      projectTitle: projectPayload.projectTitle,
      repoUrl: projectPayload.repoUrl,
      sourceType: projectPayload.sourceType,
      trigger: 'developer_detail_button',
    }).catch(() => {})
    navigateToPetAnalysis(navigate, projectPayload)
  }

  const startProjectDrag = (event) => {
    if (!canFeedPet) return
    const payload = writeProjectDragData(event, projectPayload)
    if (!payload.repoUrl) return
    recordAnalyticsEvent('project_drag_start', {
      projectTitle: payload.projectTitle,
      repoUrl: payload.repoUrl,
      sourceType: payload.sourceType,
    }).catch(() => {})
  }

  return (
    <div className="page-container tool-detail-page developer-detail-page">
      <BackButton fallbackPath="/developer/tools">{t('toolDetail.backTools')}</BackButton>

      <section
        className="tool-detail-hero"
        draggable={canFeedPet}
        onDragStart={startProjectDrag}
        aria-labelledby="developer-tool-title"
      >
        <div className="tool-detail-title-block">
          <OwnerAvatar
            className="tool-detail-avatar"
            src={tool.ownerAvatarUrl}
            owner={tool.ownerLogin || tool.name}
            size={64}
          />
          <div>
            <span className="eyebrow">{t('toolDetail.developerEyebrow')}</span>
            <h1 id="developer-tool-title">{tool.fullName || tool.name}</h1>
            <p>{developerSummary}</p>
          </div>
        </div>

        <div className="tool-detail-actions" aria-label={t('toolDetail.sourceAndDownload')}>
          {canFeedPet && (
            <button className="secondary-button pet-detail-analyze-button" type="button" onClick={feedToPet}>
              <BrainCircuit size={17} />
              {t('toolDetail.letPetAnalyze')}
            </button>
          )}
          <button
            className="primary-button"
            type="button"
            onClick={openExperience}
            disabled={!experienceUrl}
            title={!experienceUrl ? t('tools.experienceUnavailableMessage') : undefined}
          >
            <Globe2 size={17} />
            {experienceUrl ? t('toolDetail.directExperience') : t('tools.noDemo')}
          </button>
          {sourceLink && (
            <a
              className="secondary-button"
              href={sourceLink}
              target="_blank"
              rel="noreferrer"
              onClick={recordSourceLink}
            >
              <ExternalLink size={17} />
              {t('common.sourceCode')}
            </a>
          )}
          <a className="secondary-button" href={sourceHref} target="_blank" rel="noreferrer">
            <Download size={17} />
            {t('common.downloadSource')}
          </a>
          <a className="secondary-button" href={latestHref} target="_blank" rel="noreferrer">
            <Download size={17} />
            {t('common.downloadProject')}
          </a>
        </div>
      </section>

      <section className="tool-detail-summary-bar" aria-label={t('toolDetail.repoInfo')}>
        <span><Star size={16} /> Star {formatCompact(tool.stars, language)}</span>
        <span><GitFork size={16} /> Fork {formatCompact(tool.forks, language)}</span>
        <span><Code2 size={16} /> {tool.language || t('common.unlabeled')}</span>
        <span><ShieldCheck size={16} /> {tool.licenseName || t('common.unlabeled')}</span>
      </section>

      <section className="tool-detail-layout">
        <div className="tool-detail-main">
          <article className="tool-detail-section">
            <h2><TerminalSquare size={19} /> {t('toolDetail.techStack')}</h2>
            <DetailList emptyText={t('toolDetail.noExplicit')} items={valueList(tool.developer?.stack, [tool.language || t('common.unlabeled')])} />
          </article>

          <article className="tool-detail-section">
            <h2><Lightbulb size={19} /> {t('toolDetail.learningValue')}</h2>
            <p>{learningValueText}</p>
          </article>

          <article className="tool-detail-section">
            <h2><Rocket size={19} /> {t('toolDetail.reproductionDifficulty')}</h2>
            <p>{reproductionDifficultyText}</p>
          </article>

          <article className="tool-detail-section">
            <h2><Code2 size={19} /> {t('toolDetail.sourceHighlights')}</h2>
            <DetailList emptyText={t('toolDetail.noExplicit')} items={sourceHighlights} />
          </article>

          <article className="tool-detail-section">
            <h2><Star size={19} /> {t('toolDetail.resumeValue')}</h2>
            <p>{resumeValueText}</p>
          </article>

          <article className="tool-detail-section">
            <h2><Rocket size={19} /> {t('toolDetail.extensionIdeas')}</h2>
            <DetailList emptyText={t('toolDetail.noExplicit')} items={extensionIdeas} />
          </article>

          <article className="tool-detail-section">
            <h2><TerminalSquare size={19} /> {t('toolDetail.deploymentAdvice')}</h2>
            <DetailList emptyText={t('toolDetail.noExplicit')} items={advice} />
          </article>
        </div>

        <aside className="tool-detail-side">
          <section>
            <h2>{t('toolDetail.repoInfo')}</h2>
            <dl>
              <div><dt>{t('repo.fullName')}</dt><dd>{tool.fullName}</dd></div>
              <div><dt>{t('repo.primaryLanguage')}</dt><dd>{tool.language || t('common.unlabeled')}</dd></div>
              <div><dt>License</dt><dd>{tool.licenseName || t('common.unlabeled')}</dd></div>
              <div><dt>Star</dt><dd>{formatCompact(tool.stars, language)}</dd></div>
              <div><dt>Fork</dt><dd>{formatCompact(tool.forks, language)}</dd></div>
            </dl>
          </section>

          <section>
            <h2>{t('toolDetail.developerEntry')}</h2>
            {sourceLink && (
              <a href={sourceLink} target="_blank" rel="noreferrer" onClick={recordSourceLink}>
                <ExternalLink size={16} />
                {t('common.openSourceRepo')}
              </a>
            )}
            <a href={sourceHref} target="_blank" rel="noreferrer">
              <Download size={16} />
              {t('common.downloadSource')}
            </a>
            <a href={latestHref} target="_blank" rel="noreferrer">
              <Download size={16} />
              {t('common.downloadProject')}
            </a>
          </section>
        </aside>
      </section>
    </div>
  )
}

export default DeveloperToolDetailPage
